import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ExcelButtons } from '../../components/ExcelButtons';
import { MuscleGroup, ExerciseDifficultyLevel, ExerciseDto } from '@wellverse/shared-ts';

const muscleGroupColors: Record<string, string> = {
  [MuscleGroup.CHEST]: '#1890ff',
  [MuscleGroup.BACK]: '#722ed1',
  [MuscleGroup.SHOULDERS]: '#f5a623',
  [MuscleGroup.BICEPS]: '#3ecf8e',
  [MuscleGroup.TRICEPS]: '#ff4d4f',
  [MuscleGroup.LEGS]: '#eb2f96',
  [MuscleGroup.GLUTES]: '#faad14',
  [MuscleGroup.CORE]: '#52c41a',
  [MuscleGroup.FOREARMS]: '#2f54eb',
  [MuscleGroup.CALVES]: '#13c2c2',
  [MuscleGroup.FULL_BODY]: '#fa541c',
};

const muscleGroupColorList = Object.values(muscleGroupColors);

const difficultyColors: Record<string, string> = {
  [ExerciseDifficultyLevel.BEGINNER]: '#3ecf8e',
  [ExerciseDifficultyLevel.INTERMEDIATE]: '#f5a623',
  [ExerciseDifficultyLevel.ADVANCED]: '#ff4d4f',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const ExerciseList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<MuscleGroup[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<ExerciseDifficultyLevel[]>([]);
  const [selectedCompound, setSelectedCompound] = useState<boolean | undefined>(undefined);

  const { tableProps } = useTable<ExerciseDto>({
    resource: 'exercises',
    filters: {
      permanent: [
        { field: 'search', operator: 'contains', value: searchText },
        ...(selectedMuscleGroups.length
          ? [{ field: 'muscleGroup', operator: 'in' as const, value: selectedMuscleGroups }]
          : []),
        ...(selectedDifficulties.length
          ? [{ field: 'difficultyLevel', operator: 'in' as const, value: selectedDifficulties }]
          : []),
        ...(selectedCompound !== undefined
          ? [{ field: 'isCompound', operator: 'eq' as const, value: selectedCompound }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="exercises">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          mode="multiple"
          placeholder="Muscle group"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
          value={selectedMuscleGroups}
          onChange={(value) => setSelectedMuscleGroups(value)}
          options={Object.values(MuscleGroup).map((g) => ({ label: g, value: g }))}
        />
        <Select
          mode="multiple"
          placeholder="Difficulty"
          allowClear
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
          value={selectedDifficulties}
          onChange={(value) => setSelectedDifficulties(value)}
          options={Object.values(ExerciseDifficultyLevel).map((d) => ({ label: d, value: d }))}
        />
        <Select
          placeholder="Compound"
          allowClear
          style={{ width: 140 }}
          value={selectedCompound}
          onChange={(value) => setSelectedCompound(value)}
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
        />
        <ExcelButtons resource="exercise-admin/exercises" filename="exercises" resultLabels={{ imported: 'Created' }} />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          title="Translations"
          width={220}
          render={(_: any, record: ExerciseDto) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return '—';
            return (
              <Space wrap size={4}>
                {Object.entries(t).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {(data as { name?: string }).name || '—'}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="muscleGroups"
          title="Muscle Groups"
          render={(groups: MuscleGroup[]) => (
            <Space size={[4, 4]} wrap>
              {groups?.map((g, i) => (
                <Tag key={g} style={tagStyle(muscleGroupColors[g] || muscleGroupColorList[i % muscleGroupColorList.length])}>
                  {g}
                </Tag>
              ))}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="difficultyLevel"
          title="Difficulty"
          render={(level: ExerciseDifficultyLevel) => (
            <Tag style={tagStyle(difficultyColors[level] || '#898989')}>{level}</Tag>
          )}
        />
        <Table.Column
          dataIndex="isCompound"
          title="Compound"
          render={(isCompound: boolean) => (
            <Tag style={tagStyle(isCompound ? '#1890ff' : '#898989')}>
              {isCompound ? 'Yes' : 'No'}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Yes' : 'No'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: ExerciseDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="exercises" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="exercises" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="exercises" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
